<template>
  <form class="form" @submit.prevent="submit">
    <fieldset class="form__fieldset">
      <div class="form__row">
        <FormTextField label="Naam" v-model="v$.name.$model" name="name" :validator="v$.name"/>
      </div>
      <div class="form__row">
        <FormTextField label="E-mailadres" v-model="v$.email.$model" name="email" type="email" :validator="v$.email"/>
      </div>
      <div class="form__row">
        <FormNumberField label="Aantal personen (inclusief mijzelf)" v-model="v$.persons.$model" name="persons" :validator="v$.persons"/>
      </div>
      <div class="form__row">
        <FormTextareaField label="Opmerkingen" name="comment" v-model="formData.comment"/>
      </div>
      <div class="form__row" v-if="event.can_notify">
        <FormCheckbox label="Ik wil graag op de hoogte gehouden wilden worden over dit evenement" v-model="formData.notify"/>
      </div>
      <div class="form__row" v-if="event.uses_coins">
        <FormCheckbox label="Ik wil graag muntjes kopen voor dit evenement" v-model="formData.coins"/>
      </div>
      <div class="form__row" v-if="!isDisabled">
        <NuxtTurnstile v-model="token" class="mt-2 mx-auto"/>
      </div>
    </fieldset>
    <fieldset name="submit" class="form__fieldset mt-2">
      <div class="form__row">
        <button class="btn btn--flex" type="submit" :disabled="isLoading || !v$.$dirty">
          Verzenden
          <Spinner v-if="isLoading"/>
        </button>
      </div>
      <div class="form__row" v-if="!neutral">
        <span class="mt-1 field__status text--center" :class="feedbackClass">{{ feedbackMessage }}</span>
      </div>
    </fieldset>
  </form>
</template>

<script lang="ts" setup>
import { minValue, helpers, required, email, numeric } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import Event from '~/typings/event';
import RSVPResponse from '~/typings/rsvpResponse';

const props = defineProps({
  event: {
    type: Object as PropType<Event>,
    required: true
  }
});

const emit = defineEmits(['submit']);

const baseData = {
  name: '',
  email: '',
  persons: undefined,
  coins: false,
  notify: false,
  comment: ''
}

const formData = reactive({...baseData});

const { create } = useStrapi();
const { isLoading, startLoading, stopLoading } = useLoading();
const { token, isDisabled, verify } = useTurnstile();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Bedankt voor je aanmelding! Je ontvangt zo snel mogelijk een bevestiging per e-mail.',
  'Er is iets misgegaan, probeer het later nog eens.'
)

async function submit(e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;

  startLoading();

  try {
    if (!(await verify())) {
      // Verification with turnstile failed
      setStatus(false);
      return;
    }

    const response = await create<RSVPResponse>('rsvp-responses', {
      ...formData,
      event: props.event.id
    });

    handleSuccess();

    // Reset form
    Object.assign(formData, baseData);
    v$.value.$reset();
  } catch (e) {
    setStatus(false);
  } finally {
    stopLoading();
  }
}

const rules = {
  name: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    email: helpers.withMessage('Dit is geen geldig e-mailadres.', email)
  },
  persons: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    min: helpers.withMessage('Je moet minimaal 1 persoon opgeven.', minValue(1)),
    numeric: helpers.withMessage('De waarde van dit veld dient een getal zijn.', numeric)
  }
}

const v$ = useVuelidate(rules, formData);
</script>
